import { STATUS } from "./status";

/**
 * Holt das Report-JSON hinter dem Link und sagt, wie es weitergeht.
 *
 * Rein: kein React, kein Bugsnag, kein window — deshalb testbar. Was davon an
 * Bugsnag geht, entscheidet der Aufrufer (src/index.js) anhand von `status`,
 * `cause` und `error`. Loggt bewusst nichts: Bugsnag sammelt console-Ausgaben
 * als Breadcrumbs, und das JSON enthaelt die Ergebnisse einer Person.
 *
 * @param {Object} params
 * @param {{ dataUrl: string }|null} params.link - aus parseReportLink()
 * @param {typeof fetch} [params.fetchFn]
 * @returns {Promise<
 *   | { status: "ok", isV1: boolean, data: Object, type: string }
 *   | { status: string, cause: string, error?: Error }
 * >}
 */
export const loadReport = async ({ link, fetchFn = fetch }) => {
  if (!link) {
    return { status: STATUS.APP_FAILED, cause: "no_data_param" };
  }

  let response;
  try {
    response = await fetchFn(link.dataUrl);
  } catch (error) {
    return { status: STATUS.APP_FAILED, cause: "network", error };
  }

  /* S3 lehnt eine abgelaufene Signatur mit 403 ab. Die Links sind bewusst nur
     15 Minuten gueltig (pdfGenerator, Expires 60*15) — das ist der erwartete
     Weg, auf dem alte Tabs, Lesezeichen und weitergeleitete URLs enden. */
  if (response.status === 403) {
    return { status: STATUS.LINK_EXPIRED, cause: "forbidden" };
  }

  if (!response.ok) {
    // S3 antwortet mit XML — response.json() meldete hier bisher nur einen
    // nichtssagenden SyntaxError.
    return {
      status: STATUS.APP_FAILED,
      cause: `http_${response.status}`,
      error: new Error(`Report data request failed with ${response.status}`),
    };
  }

  let json;
  try {
    json = await response.json();
  } catch (error) {
    return { status: STATUS.APP_FAILED, cause: "invalid_json", error };
  }

  const isV1 =
    json?.career?.diagnostic_version === 1 || Boolean(json?.show_old_ui);
  const data = isV1 ? json : json?.generatedText;

  if (!data || !json?.type) {
    return {
      status: STATUS.APP_FAILED,
      cause: "missing_data",
      error: new Error(
        `No data or type provided / ${JSON.stringify({
          type: json?.type,
          isV1,
          hasGeneratedText: Boolean(json?.generatedText),
        })}`,
      ),
    };
  }

  return { status: "ok", isV1, data, type: json.type };
};
